import { useState } from 'react';
import { Card } from '../../../../layout/Card.jsx';
import { Icon } from '../../../../layout/Icon.jsx';
import { Pill } from '../../../../layout/Pill.jsx';
import { useAuth } from '../../../../shared/useAuth.js';
import { formatIQD } from '../../../../shared/format.js';
import {
  useDiscounts,
  useCreateDiscount,
  useUpdateDiscount,
  useDeleteDiscount
} from '../../hooks/useDiscounts.js';
import { DiscountForm } from './DiscountForm.jsx';
import { ConfirmModal } from '../ConfirmModal.jsx';

const SCOPE_LABEL = {
  sibling: 'الأشقاء',
  school: 'مدرسي',
  vip: 'VIP',
  manual: 'يدوي'
};

const TYPE_LABEL = {
  percent: 'نسبة',
  amount: 'مبلغ'
};

function formatValue(d) {
  if (d.type === 'percent') return `${Number(d.value || 0)}%`;
  return formatIQD(d.value || 0);
}

export function DiscountsList() {
  const hasPermission = useAuth((s) => s.hasPermission);
  const canEdit = hasPermission('pricing.edit');
  const { data: discounts = [], isLoading, error } = useDiscounts();
  const createD = useCreateDiscount();
  const updateD = useUpdateDiscount();
  const deleteD = useDeleteDiscount();

  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  function openCreate() {
    setEditing(null);
    setFormOpen(true);
  }
  function openEdit(d) {
    setEditing(d);
    setFormOpen(true);
  }
  function closeForm() {
    setFormOpen(false);
    setEditing(null);
  }

  async function handleSubmit(body) {
    if (editing) {
      await updateD.mutateAsync(
        { id: editing._id || editing.id, body },
        { onSuccess: () => closeForm() }
      );
    } else {
      await createD.mutateAsync(body, { onSuccess: () => closeForm() });
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[15px] font-bold">الخصومات</div>
          <div className="text-xs text-ink-3 mt-0.5">قواعد الخصومات حسب النطاق</div>
        </div>
        {canEdit ? (
          <button type="button" className="btn btn-primary" onClick={openCreate}>
            <Icon name="plus" size={14} /> خصم جديد
          </button>
        ) : null}
      </div>

      {error ? (
        <Card>
          <div className="text-red text-center py-4">تعذّر تحميل الخصومات</div>
        </Card>
      ) : isLoading ? (
        <Card>
          <div className="text-ink-3 text-center py-8">جاري التحميل...</div>
        </Card>
      ) : discounts.length === 0 ? (
        <Card>
          <div className="text-ink-3 text-center py-10 flex flex-col items-center gap-3">
            <Icon name="pricing" size={42} color="#D6CCB3" />
            <div className="text-[14px] font-semibold text-ink-2">
              لا توجد خصومات بعد — أنشئ خصمك الأول
            </div>
            {canEdit ? (
              <button type="button" className="btn btn-primary" onClick={openCreate}>
                <Icon name="plus" size={14} /> خصم جديد
              </button>
            ) : null}
          </div>
        </Card>
      ) : (
        <Card>
          <table className="w-full text-[13px]">
            <thead className="text-ink-2 text-[12px]">
              <tr className="border-b border-line">
                <th className="p-2.5 text-start">الاسم</th>
                <th className="p-2.5 text-start">النوع</th>
                <th className="p-2.5 text-start">القيمة</th>
                <th className="p-2.5 text-start">النطاق</th>
                <th className="p-2.5 text-start">الحد الأدنى</th>
                <th className="p-2.5 text-start">الحالة</th>
                <th className="p-2.5 text-end">إجراءات</th>
              </tr>
            </thead>
            <tbody>
              {discounts.map((d) => (
                <tr key={d._id || d.id} className="border-b border-line">
                  <td className="p-2.5 font-bold">{d.name}</td>
                  <td className="p-2.5">{TYPE_LABEL[d.type] || d.type}</td>
                  <td className="p-2.5 font-bold" style={{ color: '#C2570D' }}>
                    {formatValue(d)}
                  </td>
                  <td className="p-2.5">{SCOPE_LABEL[d.scope] || d.scope}</td>
                  <td className="p-2.5">{d.minQty || 0}</td>
                  <td className="p-2.5">
                    {d.isActive === false ? (
                      <Pill variant="muted">معطّل</Pill>
                    ) : (
                      <Pill variant="success">فعّال</Pill>
                    )}
                  </td>
                  <td className="p-2.5">
                    {canEdit ? (
                      <div className="flex items-center gap-1.5 justify-end">
                        <button
                          type="button"
                          className="btn"
                          style={{ padding: '4px 8px' }}
                          onClick={() => openEdit(d)}
                        >
                          <Icon name="edit" size={13} />
                        </button>
                        <button
                          type="button"
                          className="btn btn-danger"
                          style={{ padding: '4px 8px' }}
                          onClick={() => setConfirmDelete(d)}
                        >
                          <Icon name="trash" size={13} />
                        </button>
                      </div>
                    ) : (
                      <span className="text-ink-3 text-xs">للقراءة فقط</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <DiscountForm
        open={formOpen}
        discount={editing}
        onClose={closeForm}
        onSubmit={handleSubmit}
        submitting={createD.isPending || updateD.isPending}
      />

      <ConfirmModal
        open={!!confirmDelete}
        title="حذف الخصم"
        message={`هل أنت متأكد من حذف الخصم "${confirmDelete?.name || ''}"؟`}
        confirmLabel="حذف"
        variant="danger"
        loading={deleteD.isPending}
        onClose={() => setConfirmDelete(null)}
        onConfirm={async () => {
          if (!confirmDelete) return;
          await deleteD.mutateAsync(
            confirmDelete._id || confirmDelete.id,
            { onSuccess: () => setConfirmDelete(null) }
          );
        }}
      />
    </div>
  );
}
